// @ts-strict

import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { IChosenSortValue } from '@app/core/models/core-models';
import { IStatsAvailableMenus, StatisticsTableItem } from '@services/statistics/statistics.types';
import { FilterMenuOptions } from '@pages/statistics/shared/interfaces/statistics';
import { StatisticsSummaryService } from '@services/statistics/statistics-summary.service';
import { Observable, Subject } from 'rxjs';
import { delay, map, skip, takeUntil, tap } from 'rxjs/operators';
import { ColumnManagementOptionsType } from '../../shared-statistics-functions/shared-statistics-functions.component';
import { AvailableStatisticsTableColumns, TableService } from '../table.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[table-columns]',
  templateUrl: './table-columns.component.html',
  styleUrls: ['./table-columns.component.scss']
})
export class TableColumnsComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() columnManagementOptions?: ColumnManagementOptionsType;
  @Input() filterMenuOptions?: FilterMenuOptions;
  @Input() statisticsData?: StatisticsTableItem[];
  @Input() title?: string;
  @Input() currency?: string;
  @Input() appliedFiltersBar?: any;

  @Output() isColumnMenuOpen = new EventEmitter<boolean>();
  @Output() getOpenedColumnMenuHeight = new EventEmitter<number>();

  public activeSortMethod?: IChosenSortValue;

  public isDetailsTableLayout = false;

  public firstColumn!: AvailableStatisticsTableColumns;

  public availableMenus: IStatsAvailableMenus = {
    campaignMenu: false,
    adminCampaignMenu: false,
    leadIDMenu: false,
    visitIDMenu: false,
    statusMenu: false,
    leadsMenu: false,
    earnsMenu: false,
    configurationNameMenu: false,
    visitsMenu: false,
    conversionMenu: false,
    acceptanceMenu: false,
    epcMenu: false,
    rateMenu: false,
    countriesMenu: false,
    refferingMenu: false,
    mlSubMenu: false,
    salesMenu: false,
    modelMenu: false,
    createdAtMenu: false,
    registrationAtMenu: false,
    userNameMenu: false
  };

  public activeFilterMenus: IStatsAvailableMenus = {
    campaignMenu: false,
    adminCampaignMenu: false,
    leadIDMenu: false,
    visitIDMenu: false,
    statusMenu: false,
    leadsMenu: false,
    earnsMenu: false,
    configurationNameMenu: false,
    visitsMenu: false,
    conversionMenu: false,
    acceptanceMenu: false,
    epcMenu: false,
    rateMenu: false,
    countriesMenu: false,
    refferingMenu: false,
    mlSubMenu: false,
    salesMenu: false,
    modelMenu: false,
    createdAtMenu: false,
    registrationAtMenu: false,
    userNameMenu: false
  };

  public lastColumnName!: AvailableStatisticsTableColumns;

  public currentOpenMenu = '';

  public activeColumnsNames$!: Observable<AvailableStatisticsTableColumns[]>;
  private destroy$ = new Subject<boolean>();

  get isMenuClickable(): boolean {
    return true;
  }

  constructor(private statistics: StatisticsSummaryService, private readonly statisticsTableService: TableService) {}

  ngOnInit(): void {
    this.statisticsTableService
      .getSortMethod()
      .pipe(
        tap(sortMethod => {
          this.activeSortMethod = sortMethod;
          this.activeFilterMenus = this.statisticsTableService.setActiveFilterMenus(this.activeSortMethod, this.activeFilterMenus);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.statistics.isDetailsLayout
      .pipe(
        tap(res => {
          this.isDetailsTableLayout = res;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.activeColumnsNames$ = this.statisticsTableService.tableColumns$.pipe(
      map(activeColumns => {
        return activeColumns
          .map(({ results }) => results)
          .flat()
          .filter(({ checked }) => checked)
          .map(({ column }) => column);
      }),
      tap(activeColumns => (this.firstColumn = activeColumns[0]))
    );

    this.activeColumnsNames$
      .pipe(
        skip(1),
        delay(300),
        tap(() => this.saveLastColumnPosition())
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.saveLastColumnPosition();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public checkIfColumnActive = (columnName: AvailableStatisticsTableColumns, activeColumns: AvailableStatisticsTableColumns[]) => {
    return activeColumns.includes(columnName);
  };

  public menuHandler(menuName: string, clickOutside = false): void {
    if (clickOutside) {
      this.availableMenus[menuName as keyof IStatsAvailableMenus] = false;

      return;
    }

    if (menuName !== this.currentOpenMenu) this.availableMenus[this.currentOpenMenu as keyof IStatsAvailableMenus] = false;

    this.currentOpenMenu = menuName;

    this.isColumnMenuOpen.emit(!this.availableMenus[menuName as keyof IStatsAvailableMenus]);

    if (this.isMenuClickable) {
      this.availableMenus[menuName as keyof IStatsAvailableMenus] = !this.availableMenus[menuName as keyof IStatsAvailableMenus];
    }
  }

  public handleOutsideClick = () => {
    this.availableMenus[this.currentOpenMenu as keyof IStatsAvailableMenus] = false;

    this.isColumnMenuOpen.emit(false);
  };

  public saveOpenedColumnMenuHeight = (height: number) => {
    this.getOpenedColumnMenuHeight.emit(height);
  };

  public getColumnName = () => {
    let columnName: string = '';

    switch (this.title) {
      case 'stats_daily': {
        columnName = 'day';
        break;
      }
      case 'countries': {
        columnName = 'lead_country';
        break;
      }
      case 'referrals': {
        columnName = 'username';
        break;
      }
      default: {
        columnName = 'leadID';
        break;
      }
    }

    return columnName;
  };

  private saveLastColumnPosition = () => {
    const columnsWrapper = document.getElementById(' table-columns');

    if (columnsWrapper) {
      const columns = columnsWrapper.querySelectorAll('th');

      const lastColumn = columns[columns.length - 1];

      if (lastColumn) {
        const menu = lastColumn.querySelector('.column-menu');

        if (menu) (menu as HTMLElement).style.right = '0';
      }
    }
  };
}
